import {
  Shield, HardHat, BarChart3, ClipboardCheck,
  Users, FileText, AlertTriangle, CheckCircle,
  ExternalLink, Smartphone, ChevronRight, Lock, ShieldAlert, Monitor,
} from 'lucide-react'

const DEMO_URL = 'https://demo.hseqintelligence.com'

const PERSONAS = [
  { initials: 'SM', name: 'Sarah Mitchell',  role: 'Chief Safety Officer',     badge: 'Admin',          color: '#6554C0' },
  { initials: 'JC', name: 'James Chen',      role: 'Corporate Safety Manager', badge: 'Corp Safety',    color: '#1D4ED8' },
  { initials: 'LN', name: 'Lisa Nguyen',     role: 'WHS Safety Officer',       badge: 'Safety Officer', color: '#065F46' },
  { initials: 'MT', name: 'Michael Torres',  role: 'Site Supervisor',          badge: 'Supervisor',     color: '#92400E' },
  { initials: 'JT', name: 'Jack Thompson',   role: 'Field Technician',         badge: 'Field Worker',   color: '#9F1239' },
  { initials: 'RD', name: 'Ryan Davis',      role: 'Remote Field Technician',  badge: 'Field Worker',   color: '#9F1239' },
]

const FEATURES = [
  { icon: AlertTriangle, title: 'Incident Management',    desc: '10 incidents across all categories — near misses, injuries, hazards, property damage with AI severity scoring' },
  { icon: ClipboardCheck, title: 'Permits to Work',       desc: '5 active and historical permits — hot work, working at heights, confined space, excavation' },
  { icon: BarChart3, title: 'Metrics & Analytics',        desc: 'Live TRIFR, LTIFR, overdue actions, audit scores and 3 months of hours data across 3 sites' },
  { icon: FileText, title: 'Corrective Actions',          desc: '8 corrective actions linked to incidents — open, in progress, and completed with priorities' },
  { icon: HardHat, title: 'Lone Worker Monitoring',       desc: 'Real-time lone worker check-in with SOS alerts and admin monitoring dashboard' },
  { icon: Users, title: 'Full Role Hierarchy',            desc: '8 users across 6 roles — explore what each role can see and do in the platform' },
]

export default function DemoLanding() {
  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(160deg, #0A0F1E 0%, #0F172A 50%, #0A1628 100%)', fontFamily: "'Inter', -apple-system, sans-serif", color: '#F1F5F9' }}>

      {/* Nav */}
      <nav style={{ padding: '1.125rem 2rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.06)', maxWidth: 1100, margin: '0 auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.625rem' }}>
          <div style={{ width: 34, height: 34, borderRadius: 8, background: '#00C4A1', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <Shield size={18} color="#fff" />
          </div>
          <span style={{ fontWeight: 700, fontSize: '1rem', color: '#F1F5F9' }}>HSEQ Intelligence</span>
        </div>
        <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
          <a href="https://www.hseqintelligence.com" style={{ color: '#64748B', fontSize: '0.8125rem', textDecoration: 'none' }}>← Back to website</a>
          <a href="https://www.hseqintelligence.com/login" style={{ color: '#F1F5F9', fontSize: '0.8125rem', fontWeight: 600, background: 'rgba(255,255,255,0.08)', padding: '7px 14px', borderRadius: 8, textDecoration: 'none' }}>Sign in</a>
        </div>
      </nav>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 1.5rem' }}>

        {/* Hero */}
        <div style={{ textAlign: 'center', padding: '3.5rem 1.5rem 3rem' }}>
          <div style={{ display: 'inline-block', background: 'rgba(0,196,161,0.12)', border: '1px solid rgba(0,196,161,0.25)', borderRadius: 99, padding: '0.3rem 1rem', marginBottom: '1.5rem', color: '#00C4A1', fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
            Live Demo · No Signup · No Password
          </div>
          <h1 style={{ fontSize: 'clamp(2rem, 4vw, 2.75rem)', fontWeight: 800, lineHeight: 1.15, margin: '0 0 1rem', letterSpacing: '-0.025em' }}>
            Explore the full platform<br />
            <span style={{ color: '#00C4A1' }}>as any team member</span>
          </h1>
          <p style={{ color: '#64748B', fontSize: '1rem', lineHeight: 1.75, margin: '0 0 2rem', maxWidth: 520, marginLeft: 'auto', marginRight: 'auto' }}>
            A real HSEQ platform loaded with 3 months of demo data for Pacific Infrastructure Services — an Australian field services company with 3 sites and a full team.
          </p>
          <a
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.625rem',
              background: '#00C4A1', color: '#0A0F1E',
              borderRadius: 12, padding: '0.9rem 2.25rem',
              fontSize: '1rem', fontWeight: 800, textDecoration: 'none',
              boxShadow: '0 4px 24px rgba(0,196,161,0.3)',
              letterSpacing: '-0.01em',
            }}
          >
            Enter Demo <ExternalLink size={16} />
          </a>
          <p style={{ color: '#334155', fontSize: '0.75rem', marginTop: '0.75rem' }}>
            Opens at demo.hseqintelligence.com — click any persona to enter instantly
          </p>
        </div>

        {/* Persona picker preview */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, padding: '1.75rem', marginBottom: '3rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1.25rem' }}>
            <span style={{ fontSize: 10, fontWeight: 800, padding: '2px 8px', borderRadius: 4, background: '#1B2B4B', color: '#00C4A1', letterSpacing: '0.07em', border: '1px solid rgba(0,196,161,0.3)' }}>DEMO</span>
            <span style={{ fontWeight: 700, fontSize: '0.9375rem' }}>Sign in as — no password required</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginBottom: '1rem' }}>
            {PERSONAS.map(p => (
              <div key={p.name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 12, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', flexShrink: 0, background: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 700, color: 'white' }}>
                  {p.initials}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.8125rem', color: '#E2E8F0', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.name}</div>
                  <div style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 999, background: `${p.color}25`, color: p.color, display: 'inline-block', marginTop: 2 }}>
                    {p.badge}
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p style={{ color: '#475569', fontSize: '0.75rem', margin: 0, textAlign: 'center' }}>
            Each role has different permissions and views — try a few to see the platform from every angle
          </p>
        </div>

        {/* What's included */}
        <div style={{ marginBottom: '3rem' }}>
          <h2 style={{ color: '#E2E8F0', fontWeight: 700, fontSize: '1.125rem', marginBottom: '1.25rem', textAlign: 'center' }}>What's loaded in the demo</h2>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '0.875rem' }}>
            {FEATURES.map(f => (
              <div key={f.title} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 12, padding: '1rem 1.125rem', display: 'flex', gap: '0.875rem', alignItems: 'flex-start' }}>
                <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(0,196,161,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  <f.icon size={17} color="#00C4A1" />
                </div>
                <div>
                  <div style={{ color: '#E2E8F0', fontWeight: 700, fontSize: '0.875rem', marginBottom: '0.25rem' }}>{f.title}</div>
                  <div style={{ color: '#475569', fontSize: '0.75rem', lineHeight: 1.6 }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Mobile app section — two APK downloads */}
        <div style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)', borderRadius: 16, padding: '1.75rem', marginBottom: '3rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: '1.25rem' }}>
            <Smartphone size={18} color="#00C4A1" />
            <span style={{ fontWeight: 700, fontSize: '1rem' }}>Download the Android App</span>
          </div>

          {/* Two download cards */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1.5rem' }}>

            {/* Demo APK */}
            <div style={{ background: 'rgba(0,196,161,0.06)', border: '1px solid rgba(0,196,161,0.25)', borderRadius: 12, padding: '1.25rem' }}>
              <div style={{ display: 'inline-block', background: 'rgba(0,196,161,0.15)', color: '#00C4A1', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 99, marginBottom: '0.625rem' }}>
                Demo
              </div>
              <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#F1F5F9', marginBottom: '0.375rem' }}>
                Demo APK
              </div>
              <p style={{ color: '#64748B', fontSize: '0.75rem', lineHeight: 1.6, margin: '0 0 1rem' }}>
                Connects to the demo environment. Includes a persona picker — no account needed. Try all roles instantly.
              </p>
              <a
                href="https://demo.hseqintelligence.com/download/hseq-intelligence-demo.apk"
                download
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#00C4A1', fontSize: '0.8125rem', fontWeight: 700, textDecoration: 'none', border: '1px solid rgba(0,196,161,0.4)', padding: '7px 14px', borderRadius: 8, background: 'rgba(0,196,161,0.08)' }}
              >
                Download Demo APK <ChevronRight size={13} />
              </a>
            </div>

            {/* Production APK */}
            <div style={{ background: 'rgba(99,102,241,0.06)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 12, padding: '1.25rem' }}>
              <div style={{ display: 'inline-block', background: 'rgba(99,102,241,0.15)', color: '#818CF8', fontSize: '0.6875rem', fontWeight: 700, letterSpacing: '0.07em', textTransform: 'uppercase', padding: '2px 8px', borderRadius: 99, marginBottom: '0.625rem' }}>
                Production
              </div>
              <div style={{ fontWeight: 700, fontSize: '0.9375rem', color: '#F1F5F9', marginBottom: '0.375rem' }}>
                Production APK
              </div>
              <p style={{ color: '#64748B', fontSize: '0.75rem', lineHeight: 1.6, margin: '0 0 1rem' }}>
                Connects to your live tenant at hseqintelligence.com. Requires a real account login. For operational use.
              </p>
              <a
                href="https://www.hseqintelligence.com/download/hseq-intelligence.apk"
                download
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#818CF8', fontSize: '0.8125rem', fontWeight: 700, textDecoration: 'none', border: '1px solid rgba(99,102,241,0.4)', padding: '7px 14px', borderRadius: 8, background: 'rgba(99,102,241,0.08)' }}
              >
                Download Production APK <ChevronRight size={13} />
              </a>
            </div>
          </div>

          {/* Install steps */}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.06)', paddingTop: '1rem' }}>
            <div style={{ color: '#475569', fontSize: '0.6875rem', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '0.625rem' }}>Install steps (both APKs)</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.375rem' }}>
              {[
                'Download the APK to your phone',
                'Settings → Apps → Allow installs from Chrome',
                'Open the downloaded APK and tap Install',
                'Log in (demo: use persona picker · production: your account)',
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-start' }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,196,161,0.1)', border: '1px solid rgba(0,196,161,0.25)', color: '#00C4A1', fontSize: '0.6rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                    {i + 1}
                  </div>
                  <p style={{ color: '#64748B', fontSize: '0.75rem', margin: 0, lineHeight: 1.5 }}>{s}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Lone Worker end-to-end guide */}
        <div style={{ background: 'rgba(239,68,68,0.05)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 16, padding: '1.75rem', marginBottom: '3rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: '1rem' }}>
            <div style={{ width: 36, height: 36, borderRadius: 9, background: 'rgba(239,68,68,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <ShieldAlert size={18} color="#EF4444" />
            </div>
            <div>
              <div style={{ fontWeight: 800, fontSize: '1rem', color: '#F1F5F9' }}>Test Lone Worker — full end-to-end</div>
              <div style={{ color: '#64748B', fontSize: '0.75rem', marginTop: 1 }}>Requires the demo APK on a phone + a browser open simultaneously</div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', flexWrap: 'wrap' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.75rem' }}>
                <Smartphone size={14} color="#EF4444" />
                <span style={{ fontWeight: 700, fontSize: '0.8125rem', color: '#E2E8F0' }}>On the phone (demo APK)</span>
              </div>
              {[
                'Sign in as Ryan Davis — Field Worker',
                'Tap PANIC / SOS on the home screen',
                'Select an activity type and tap Start Session',
                'Session is now active and being monitored',
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(239,68,68,0.15)', border: '1px solid rgba(239,68,68,0.4)', color: '#EF4444', fontSize: '0.625rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    {i + 1}
                  </div>
                  <p style={{ color: '#64748B', fontSize: '0.75rem', margin: 0, lineHeight: 1.6 }}>{s}</p>
                </div>
              ))}
            </div>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: '0.75rem' }}>
                <Monitor size={14} color="#00C4A1" />
                <span style={{ fontWeight: 700, fontSize: '0.8125rem', color: '#E2E8F0' }}>In a browser (admin monitor)</span>
              </div>
              {[
                <>Open <a href={DEMO_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#00C4A1', textDecoration: 'none', fontWeight: 700 }}>demo.hseqintelligence.com</a> — same demo site</>,
                'Sign in as Sarah Mitchell — Admin',
                'Go to Lone Worker in the sidebar',
                "Ryan's session appears live on the monitor",
              ].map((s, i) => (
                <div key={i} style={{ display: 'flex', gap: '0.625rem', alignItems: 'flex-start', marginBottom: '0.5rem' }}>
                  <div style={{ width: 18, height: 18, borderRadius: '50%', background: 'rgba(0,196,161,0.1)', border: '1px solid rgba(0,196,161,0.3)', color: '#00C4A1', fontSize: '0.625rem', fontWeight: 800, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                    {i + 1}
                  </div>
                  <p style={{ color: '#64748B', fontSize: '0.75rem', margin: 0, lineHeight: 1.6 }}>{s}</p>
                </div>
              ))}
              <a
                href={`${DEMO_URL}/login`}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#00C4A1', fontSize: '0.8125rem', fontWeight: 700, textDecoration: 'none', border: '1px solid rgba(0,196,161,0.3)', padding: '7px 14px', borderRadius: 8, background: 'rgba(0,196,161,0.06)', marginTop: '0.5rem' }}
              >
                Open Admin Monitor <ExternalLink size={13} />
              </a>
            </div>
          </div>
          <div style={{ marginTop: '1rem', padding: '10px 14px', borderRadius: 8, background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)' }}>
            <span style={{ color: '#FCA5A5', fontSize: '0.75rem', fontWeight: 600 }}>Important: </span>
            <span style={{ color: '#64748B', fontSize: '0.75rem' }}>Both the phone app and the browser must use <strong style={{ color: '#E2E8F0' }}>demo.hseqintelligence.com</strong> — they share the same demo database. The production site (hseqintelligence.com) is separate.</span>
          </div>
        </div>

        {/* CTA footer */}
        <div style={{ textAlign: 'center', paddingBottom: '4rem' }}>
          <a
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.625rem',
              background: '#00C4A1', color: '#0A0F1E',
              borderRadius: 12, padding: '0.9rem 2.25rem',
              fontSize: '1rem', fontWeight: 800, textDecoration: 'none',
              boxShadow: '0 4px 24px rgba(0,196,161,0.3)',
            }}
          >
            Enter Demo <ExternalLink size={16} />
          </a>
          <div style={{ display: 'flex', gap: '1.5rem', justifyContent: 'center', marginTop: '1.25rem', flexWrap: 'wrap' }}>
            {[
              [Lock, 'No signup required'],
              [CheckCircle, 'No credit card'],
              [Users, '8 roles to explore'],
            ].map(([Icon, label]) => (
              <div key={label as string} style={{ display: 'flex', alignItems: 'center', gap: 5, color: '#475569', fontSize: '0.8125rem' }}>
                <Icon size={13} color="#00C4A1" /> {label as string}
              </div>
            ))}
          </div>
        </div>

      </div>
    </div>
  )
}
